import { ArrowDownUp } from "lucide-react";
import { useId } from "react";

import { formatSendMoney } from "@/modules/dashboard/dispersions/components/WalletSend/formatSendMoney";
import { CurrencyFlag } from "@/modules/dashboard/wallets/components/CurrencyFlag";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import useTranslations from "@/shared/lang/useTranslations";

import type { SwapDestination } from "../../utils/walletSwap";

export interface SwapRouteStripProps {
  origins: RuntimeWallet[];
  origin: RuntimeWallet;
  onOriginChange: (walletId: string) => void;
  destinations: SwapDestination[];
  destination: RuntimeWallet | null;
  onDestinationChange: (walletId: string) => void;
  /** Puts the destination wallet first; off when it could not be an origin (frozen). */
  onFlip: () => void;
  canFlip: boolean;
}

const LABEL = "w-14 shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

/** Which wallet the money leaves and which it lands in, with the flip between them. */
export function SwapRouteStrip({
  origins,
  origin,
  onOriginChange,
  destinations,
  destination,
  onDestinationChange,
  onFlip,
  canFlip,
}: SwapRouteStripProps) {
  const { t, activeLanguage } = useTranslations("swaps");
  const fromId = useId();
  const toId = useId();
  const walletLabel = (wallet: RuntimeWallet, suffix?: string) => (
    <span className="flex items-center gap-2">
      <CurrencyFlag currency={wallet.currency} />
      {t("convert_wallet", { currency: wallet.currency })}
      {suffix && <span className="text-xs text-muted-foreground">· {suffix}</span>}
    </span>
  );
  const balance = (wallet: RuntimeWallet) =>
    formatSendMoney(wallet.balance_available, wallet.currency, activeLanguage);

  // Both rows leave the right edge to the flip button, which sits on the line between them; each balance
  // goes under its select, so nothing in a row competes with the button for that space on a phone.
  return (
    <section className="relative rounded-xl border border-border bg-card">
      <div className="flex items-start gap-3 py-3 pr-14 pl-3">
        <span id={fromId} className={`${LABEL} pt-2`}>
          {t("convert_from")}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <ColocaSelect
            value={origin.id}
            onValueChange={(value) => value && onOriginChange(value)}
            options={origins.map((wallet) => ({ value: wallet.id, label: walletLabel(wallet) }))}
            size="sm"
            className="w-full"
            aria-labelledby={fromId}
          />
          <p className="font-mono text-xs text-muted-foreground tabular-nums">
            {t("convert_available", { amount: balance(origin) })}
          </p>
        </div>
      </div>
      <div className="relative h-px bg-border">
        <ColocaIconButton
          icon={ArrowDownUp}
          size="sm"
          variant="secondary"
          aria-label={t("convert_flip")}
          title={t("convert_flip")}
          onClick={onFlip}
          disabled={!canFlip}
          className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full border border-border bg-card shadow-sm"
        />
      </div>
      <div className="flex items-start gap-3 py-3 pr-14 pl-3">
        <span id={toId} className={`${LABEL} pt-2`}>
          {t("convert_to")}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <ColocaSelect
            value={destination?.id ?? ""}
            onValueChange={(value) => value && onDestinationChange(value)}
            options={destinations.map(({ wallet, disabled }) => ({
              value: wallet.id,
              label: walletLabel(wallet, disabled ? t("convert_frozen") : undefined),
              disabled,
            }))}
            size="sm"
            className="w-full"
            aria-labelledby={toId}
          />
          {destination && (
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {t("convert_balance", { amount: balance(destination) })}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
